// --- Single Source of Truth for Permissions ---
/**
 * Array containing all valid permissions in the system.
 * This serves as the single source of truth for permissions.
 * @readonly
 */
const ALL_PERMISSIONS_ARRAY = [
  'download_format_wav',
  'download_premium_lut',
  'download_premium_mg',
  'download_premium_music',
  'download_premium_sfx',
  'download_premium_stock_video',
  'download_stock_video_format_1080p',
  'download_stock_video_format_4k',
  'download_stock_video_format_720p',
  'safelist_yt',
  'test_permission',
  'unlimited_downloads_lut',
  'unlimited_downloads_mg',
  'unlimited_downloads_music',
  'unlimited_downloads_sfx',
  'unlimited_downloads_stock_video',
] as const;

// --- Single Source of Truth for Roles ---
/**
 * Array containing all valid user roles in the system.
 * This serves as the single source of truth for roles.
 * @readonly
 */
const ALL_ROLES_ARRAY = [
  'User',
  'Free',
  'Premium',
  'Business',
  'Essentials',
  'Creator',
  'Pro',
  'LoggedIn',
  'LoggedOut',
  'Admin',
] as const;

// --- Derived Permission Type ---
/**
 * Represents all possible user permissions in the system.
 * Automatically derived from ALL_PERMISSIONS_ARRAY.
 */
export type Permission = (typeof ALL_PERMISSIONS_ARRAY)[number];

// --- Derived Runtime Set for Validation ---
/**
 * A Set containing all valid permissions for runtime validation.
 * @readonly
 */
const ALL_PERMISSIONS_SET: ReadonlySet<Permission> = new Set(
  ALL_PERMISSIONS_ARRAY
);

/**
 * Type guard to check if a string is a valid Permission.
 * @param value The value to check.
 * @returns True if the value is a valid Permission string, false otherwise.
 */
export function isPermission(value: unknown): value is Permission {
  // Check against the derived Set
  return (
    typeof value === 'string' && ALL_PERMISSIONS_SET.has(value as Permission)
  );
}

// --- Derived Role Type ---
/**
 * Represents all possible user roles in the system.
 * Automatically derived from ALL_ROLES_ARRAY.
 */
export type Role = (typeof ALL_ROLES_ARRAY)[number];

// --- Derived Runtime Set for Validation ---
/**
 * A Set containing all valid roles for runtime validation.
 * @readonly
 */
const ALL_ROLES_SET: ReadonlySet<Role> = new Set(ALL_ROLES_ARRAY);

/**
 * Type guard to check if a string is a valid Role.
 * @param value The value to check.
 * @returns True if the value is a valid Role string, false otherwise.
 */
export function isRole(value: unknown): value is Role {
  // Check against the derived Set
  return typeof value === 'string' && ALL_ROLES_SET.has(value as Role);
}

/**
 * Common interface for permission checking functionality.
 * Implemented by both client and server authentication systems.
 */
export interface PermissionChecker {
  /**
   * Checks if the user has all of the specified permissions.
   * @param permissions - Array of permissions to check
   * @returns True if the user has all the permissions, false otherwise
   */
  hasAllPermissions: (permissions: readonly Permission[]) => boolean;

  /**
   * Checks if the user has any of the specified permissions.
   * @param permissions - Array of permissions to check
   * @returns True if the user has at least one of the permissions, false otherwise
   */
  hasAnyPermission: (permissions: readonly Permission[]) => boolean;

  /**
   * Checks if the user has a specific permission.
   * @param permission - The permission to check for, or null
   * @returns True if the user has the permission, false otherwise
   */
  hasPermission: (permission: Permission | null) => boolean;

  /**
   * Checks if the user has a specific role.
   * @param role - The role to check for, or null
   * @returns True if the user has the role, false otherwise
   */
  hasRole: (role: Role | null) => boolean;

  /**
   * Indicates whether the current user is authenticated.
   */
  readonly isAuthenticated: boolean;
  /**
   * The set or array of permissions assigned to the current user.
   */
  readonly permissions: ReadonlySet<Permission> | readonly Permission[];

  /**
   * Array of permissions assigned to the current user.
   */
  readonly permissionsArray: readonly Permission[];

  /**
   * The set or array of roles assigned to the current user.
   */
  readonly roles: ReadonlySet<Role> | readonly Role[];

  /**
   * Array of roles assigned to the current user.
   */
  readonly rolesArray: readonly Role[];

  /**
   * The unique identifier of the current user.
   */
  readonly userId?: string;
}

// --- Auth Token Payload (uses derived types) ---
/**
 * Defines the expected shape of the payload within a verified JWT.
 */
export interface AuthTokenPayload {
  /**
   * Token expiration timestamp
   */
  exp?: number;

  /**
   * Token issued at timestamp
   */
  iat?: number;

  /**
   * Array of permissions granted to the user
   */
  permissions?: Permission[];

  /**
   * Array of roles assigned to the user
   */
  role?: Role[];
  /**
   * User ID stored in the token subject claim
   */
  sub?: string;

  /**
   * Additional custom claims that may be present in the token
   */
  [key: string]: unknown;
}
